import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { translate } from '../../form-designer/utils/form-manager.utils';
import { decodeString } from '../../utils/commonUtils';
import {
  DATE_FORMAT,
  DATE_FORMAT_WITH_TIMESTAMP,
  dateToDatabaseDateString,
} from '../../utils/dateUtils';
import { safeFormatDecimalValue } from '../../utils/savedSearchUtils';
import { DEFAULT_SELECT_VALUE } from '../AdvancedSearchModal/Criterion/constants';
import PopupText from '../PopupText';

const isDynamicValue = (value) => typeof value === 'string' && /\{\|.*\|\}/.test(value);

export function apiCriterionToPopoverCriterion(criterion) {
  return {
    id: criterion.id || uuidv4(),
    openGroup: criterion.OpenGroup === '(' ? criterion.OpenGroup : DEFAULT_SELECT_VALUE,
    closeGroup: criterion.CloseGroup === ')' ? criterion.CloseGroup : DEFAULT_SELECT_VALUE,
    value: isDynamicValue(criterion.Value) ? '' : criterion.Value,
    column: criterion.FieldName,
    operator: criterion.Condition,
    relationalOperator: typeof criterion.Operator === 'string' && criterion.Operator.toLowerCase(),
    allowEditting:
      typeof criterion.AllowEditing === 'string'
        ? criterion.AllowEditing.toLowerCase().localeCompare('true') === 0
        : typeof criterion.AllowEditing === 'boolean' && criterion.AllowEditing,
    columnType: criterion.ColumnType,
    enableLookup: (criterion.EnableLookup || '').toLowerCase() === 'true',
  };
}

export function filterToPopoverCriterion(filter) {
  return {
    id: filter.id || uuidv4(),
    openGroup: filter.openGroup === 'false' ? DEFAULT_SELECT_VALUE : '(',
    closeGroup: filter.closeGroup === 'false' ? DEFAULT_SELECT_VALUE : ')',
    value: isDynamicValue(filter.value) ? '' : filter.value,
    column: filter.fieldName,
    operator: filter.sqlOperator,
    relationalOperator:
      typeof filter.relationalOperator === 'string' && filter.relationalOperator.toLowerCase(),
    allowEditting: false,
    enableLookup: false,
    isCustom: true,
  };
}

// Had to make the code safer because of Apollo Table state management issues
export function createStringSearchFilter(accessor) {
  return function(row = {}, filters = {}) {
    const rowVal = row[accessor]?.toUpperCase() ?? '';
    const filterVal = filters[accessor]?.toUpperCase() ?? '';

    return rowVal?.includes(filterVal);
  };
}

export const compareBooleans = (accessor, sortOrder) => {
  return (rowA, rowB) => {
    const boolA = Boolean(rowA[accessor]);
    const boolB = Boolean(rowB[accessor]);

    let result;
    if (boolA === boolB) {
      result = 0;
    } else if (boolA) {
      result = 1;
    } else {
      result = -1;
    }

    return sortOrder === 'asc' ? result : -result;
  };
};

export const createCustomCell = (
  { dataType, accessor, formatType },
  translations = {},
  requiresTimeStamp
) => ({ row }) => {
  if (_.isNil(row[accessor])) {
    return null;
  }

  if (accessor === 'SSE_STATUS') {
    dataType = 'System.String';
  }

  switch (dataType) {
    case 'System.Byte':
    case 'System.Boolean': {
      const boolValue = !!(
        row[accessor] === 'true' ||
        row[accessor] === true ||
        row[accessor] === '1' ||
        row[accessor] === 1
      );
      return translate(translations, boolValue ? 'Yes' : 'No');
    }
    case 'System.DateTime': {
      const date = moment(row[accessor]);
      return date.isValid()
        ? date.format(requiresTimeStamp ? DATE_FORMAT_WITH_TIMESTAMP : DATE_FORMAT)
        : row[accessor];
    }
    case 'System.Decimal': {
      return safeFormatDecimalValue(row[accessor], formatType);
    }
    case 'System.String':
    default: {
      return <PopupText text={decodeString(row[accessor])} />;
    }
  }
};

// Had to make the code safer because of Apollo Table state management issues
export function compareStrings(accessor, sortOrder) {
  return function(rowA, rowB) {
    const stringA = (rowA[accessor] || '').toUpperCase();
    const stringB = (rowB[accessor] || '').toUpperCase();

    if (sortOrder === 'asc') {
      if (stringA < stringB) {
        return -1;
      }

      if (stringA > stringB) {
        return 1;
      }

      return 0;
    }
    if (stringA < stringB) {
      return 1;
    }

    if (stringA > stringB) {
      return -1;
    }

    return 0;
  };
}

export function mapAdvancedSearchCriteria(advancedSearchCriteria) {
  return Array.isArray(advancedSearchCriteria)
    ? advancedSearchCriteria.map((criterion) => ({
        OpenGroup: criterion.openGroup === DEFAULT_SELECT_VALUE ? ' ' : criterion.openGroup,
        CloseGroup: criterion.closeGroup === DEFAULT_SELECT_VALUE ? ' ' : criterion.closeGroup,
        Value: moment.isMoment(criterion.value)
          ? dateToDatabaseDateString(criterion.value)
          : criterion.value,
        FieldName: criterion.column,
        Condition: criterion.operator,
        Operator: criterion.relationalOperator,
        AllowEditing: !_.isNil(criterion.allowEditting)
          ? criterion.allowEditting.toString()
          : 'false',
        ColumnType: criterion.columnType,
        EnableLookup: !_.isNil(criterion.enableLookup)
          ? criterion.enableLookup.toString()
          : 'false',
      }))
    : undefined;
}

export function prepareAdvancedSearchCriteriaToSave(advancedSearchCriteria) {
  return Array.isArray(advancedSearchCriteria)
    ? advancedSearchCriteria.map((criterion) => ({
        openGroup: criterion.OpenGroup === ' ' ? DEFAULT_SELECT_VALUE : criterion.OpenGroup,
        closeGroup: criterion.CloseGroup === ' ' ? DEFAULT_SELECT_VALUE : criterion.CloseGroup,
        value: criterion.Value,
        column: criterion.FieldName,
        operator: criterion.Condition,
        relationalOperator: criterion.Operator,
        allowEditting: criterion.AllowEditing === 'true' ? true : false,
        columnType: criterion.ColumnType,
        enableLookup: criterion.EnableLookup === 'true' ? true : false,
      }))
    : undefined;
}
